import React, { useEffect, useRef, useState } from 'react';
import { API } from '../api';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { Field, controlClass } from '../ui/Field';
import { useToast } from '../ui/Toast';
import useAsync from '../ui/useAsync';

// value: what the API takes; token: how the team appears in the cron command.
const TEAMS = [
  { value: 'DBA', token: 'dba', label: 'DBA' },
  { value: 'DevOps', token: 'devops', label: 'DevOps' },
  { value: 'Enterprise', token: 'enterprise', label: 'Enterprise' },
];
const RUNS = [
  { value: 'whole_ticketing_process', label: 'Monthly', name: 'monthly ticketing' },
  { value: 'critical_ticketing_process', label: 'Daily critical', name: 'daily critical ticketing' },
];
const SLOTS = TEAMS.flatMap((team) => RUNS.map((run) => ({ id: `${team.value}|${run.value}`, team, run })));
const MONTHLY = RUNS[0].value;

const EMPTY_FORM = { date: '', hour: '', minute: '' };
const LIMITS = { date: [1, 31, 'Day'], hour: [0, 23, 'Hour'], minute: [0, 59, 'Minute'] };

// The cron rows the API would change, found the same way as in api/src/handlers/cron.rs.
// Returns { [slot id]: { minute, hour, day, duplicate? } }.
function parseSchedule(text) {
  const slots = {};
  String(text || '').split('\n').forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) return;
    const parts = trimmed.split(/\s+/);
    if (parts.length < 6) return;
    const [minute, hour, day] = parts;
    const command = parts.slice(5).join(' ');
    const critical = command.includes('severity=critical');
    let run = null;
    if (critical && day === '*') run = 'critical_ticketing_process';
    else if (!critical && /^\d+$/.test(day)) run = MONTHLY;
    if (!run) return;
    TEAMS.filter((team) => command.includes(`/api/tickets/${team.token}`)).forEach((team) => {
      const id = `${team.value}|${run}`;
      slots[id] = slots[id] ? { ...slots[id], duplicate: true } : { minute, hour, day };
    });
  });
  return slots;
}

const pad = (v) => (/^\d+$/.test(String(v)) ? String(v).padStart(2, '0') : v);
const timeOf = ({ hour, minute }) => `${pad(hour)}:${pad(minute)}`;
const describeRun = (run, s) => (run === MONTHLY ? `Day ${s.day} at ${timeOf(s)}` : `Every day at ${timeOf(s)}`);

const parseNumber = (value) => {
  if (value === '') return null;
  return /^\d+$/.test(String(value)) ? Number(value) : Number.NaN;
};
const fieldError = (key, value) => {
  if (value === '') return null;
  const [min, max, label] = LIMITS[key];
  const n = parseNumber(value);
  return Number.isInteger(n) && n >= min && n <= max ? null : `${label} must be between ${min} and ${max}.`;
};

function SlotCell({ slot, state, checked, tabbable, onSelect, onMove, refFor }) {
  const current = state.slots?.[slot.id];
  let text;
  let note = null;
  if (state.loading) text = <span className="inline-block w-20 h-3 skeleton rounded-badge" aria-hidden="true" />;
  else if (!state.slots) text = <span className="text-graphite">Unknown</span>;
  else if (!current) text = <span className="text-graphite">Not scheduled</span>;
  else {
    text = slot.run.value === MONTHLY ? `Day ${current.day} at ${timeOf(current)}` : timeOf(current);
    if (current.duplicate) note = 'More than one row';
  }
  const spoken = state.loading ? 'loading' : current ? describeRun(slot.run.value, current) : state.slots ? 'not scheduled' : 'current time unknown';

  return (
    <button
      ref={refFor}
      type="button"
      role="radio"
      aria-checked={checked}
      aria-label={`${slot.team.label} ${slot.run.name}, ${spoken}`}
      tabIndex={tabbable ? 0 : -1}
      onClick={onSelect}
      onKeyDown={onMove}
      className={`relative h-12 px-3 text-left border-l border-t border-rule outline-none focus-visible:z-[1] focus-visible:shadow-[inset_0_0_0_2px_rgb(var(--signal))] ${checked ? 'bg-signal-soft text-signal font-semibold shadow-[inset_0_0_0_1.5px_rgb(var(--signal))]' : 'text-ink hover:bg-hover'}`}
    >
      <span className="block text-body tabular-nums">{text}</span>
      {note && <span className="block text-small text-crit font-normal">{note}</span>}
    </button>
  );
}

function SlotGrid({ state, selected, onSelect }) {
  const refs = useRef([]);
  const index = SLOTS.findIndex((s) => s.id === selected);
  const move = (i) => (event) => {
    const step = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: RUNS.length, ArrowUp: -RUNS.length }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (i + step + SLOTS.length) % SLOTS.length;
    onSelect(SLOTS[next].id);
    refs.current[next]?.focus();
  };

  return (
    <div role="radiogroup" aria-label="Schedule to change" className="grid grid-cols-[96px_1fr_1fr] border-b border-r border-rule rounded-panel overflow-hidden">
      <div className="h-8 border-l border-t border-rule" aria-hidden="true" />
      {RUNS.map((run) => (
        <div key={run.value} className="h-8 px-3 flex items-center border-l border-t border-rule text-small font-semibold text-graphite" aria-hidden="true">{run.label}</div>
      ))}
      {TEAMS.map((team) => (
        <React.Fragment key={team.value}>
          <div className="h-12 px-3 flex items-center border-l border-t border-rule text-body font-semibold text-ink" aria-hidden="true">{team.label}</div>
          {SLOTS.filter((s) => s.team === team).map((slot) => {
            const i = SLOTS.indexOf(slot);
            return (
              <SlotCell
                key={slot.id}
                slot={slot}
                state={state}
                checked={slot.id === selected}
                tabbable={index === -1 ? i === 0 : i === index}
                onSelect={() => onSelect(slot.id)}
                onMove={move(i)}
                refFor={(el) => { refs.current[i] = el; }}
              />
            );
          })}
        </React.Fragment>
      ))}
    </div>
  );
}

function NumberField({ id, label, value, placeholder, error, min, max, disabled, onChange }) {
  return (
    <Field id={id} label={label} error={error}>
      <input
        id={id}
        type="number"
        inputMode="numeric"
        min={min}
        max={max}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        aria-invalid={Boolean(error)}
        aria-describedby={error ? `${id}-error` : 'schedule-hint'}
        className={`${controlClass({ invalid: Boolean(error) })} tabular-nums placeholder:text-graphite/70`}
      />
    </Field>
  );
}

/**
 * Side panel that changes when a team's scheduled ticket run starts (Manage › Schedules).
 * The current times come from the server's cron file; empty fields keep the current value.
 */
export default function SchedulePanel({ open, onClose }) {
  const toast = useToast();
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitError, setSubmitError] = useState(null);
  const [busy, setBusy] = useState(false);
  const schedule = useAsync(() => API.GET_CRON_SCHEDULE(), open ? 'cron-open' : 'cron-closed', { enabled: open });

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setForm(EMPTY_FORM);
    setSubmitError(null);
  }, [open]);

  const slots = schedule.data ? parseSchedule(schedule.data.schedule) : null;
  const state = { loading: schedule.loading, slots };
  const slot = SLOTS.find((s) => s.id === selected);
  const monthly = slot?.run.value === MONTHLY;
  const current = slot && slots?.[slot.id];
  const errors = {
    date: monthly ? fieldError('date', form.date) : null,
    hour: fieldError('hour', form.hour),
    minute: fieldError('minute', form.minute),
  };
  const valid = Boolean(slot) && !errors.date && !errors.hour && !errors.minute;

  const set = (key) => (value) => {
    setSubmitError(null);
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const select = (id) => {
    setSubmitError(null);
    setSelected(id);
    setForm(EMPTY_FORM);
  };

  const changed = form.date !== '' || form.hour !== '' || form.minute !== '';
  const next = current && {
    day: form.date !== '' ? Number(form.date) : current.day,
    hour: form.hour !== '' ? Number(form.hour) : current.hour,
    minute: form.minute !== '' ? Number(form.minute) : current.minute,
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setSubmitError(null);
    try {
      await API.UPDATE_CRON_SCHEDULE({
        team: slot.team.value,
        ticketing_type: slot.run.value,
        date: monthly && form.date !== '' ? parseNumber(form.date) : null,
        hour: form.hour === '' ? null : parseNumber(form.hour),
        minute: form.minute === '' ? null : parseNumber(form.minute),
      });
      toast.success(`Saved the ${slot.team.label} ${slot.run.name} schedule.`);
      onClose();
    } catch (error) {
      setSubmitError(error.message || 'The request did not complete.');
    } finally {
      setBusy(false);
    }
  };

  const placeholder = (key) => (current ? String(current[key === 'date' ? 'day' : key]) : 'Keep');

  return (
    <SidePanel
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Ticketing schedules"
      description="When each team's automatic ticket runs start."
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="schedule-form" variant="primary" disabled={!valid || busy}>
            {busy ? 'Saving…' : 'Save schedule'}
          </Button>
        </>
      )}
    >
      <form id="schedule-form" onSubmit={submit} noValidate className="space-y-5">
        <div>
          <SlotGrid state={state} selected={selected} onSelect={select} />
          {schedule.error ? (
            <p className="mt-2 text-small text-graphite">
              Current times could not be loaded: <span className="font-mono text-ink">{schedule.error.message}</span>{' '}
              <button type="button" onClick={schedule.retry} className="text-signal hover:underline">Try again</button>
            </p>
          ) : (
            <p className="mt-2 text-small text-graphite">Times use the server&apos;s clock.</p>
          )}
        </div>

        {slot ? (
          <fieldset disabled={busy} className="pt-4 border-t border-rule">
            <legend className="sr-only">New time</legend>
            <h3 className="mb-3 text-section text-ink">Change {slot.team.label} {slot.run.name}</h3>
            <div className={`grid gap-3 ${monthly ? 'grid-cols-3' : 'grid-cols-2'}`}>
              {monthly && <NumberField id="schedule-date" label="Day of month" min={1} max={31} value={form.date} placeholder={placeholder('date')} error={errors.date} onChange={set('date')} />}
              <NumberField id="schedule-hour" label="Hour" min={0} max={23} value={form.hour} placeholder={placeholder('hour')} error={errors.hour} onChange={set('hour')} />
              <NumberField id="schedule-minute" label="Minute" min={0} max={59} value={form.minute} placeholder={placeholder('minute')} error={errors.minute} onChange={set('minute')} />
            </div>
            <p id="schedule-hint" className="mt-2 text-small text-graphite">
              Empty fields keep their current value.{!monthly && ' This run starts every day.'}
            </p>
            {slots && (!current || current.duplicate) && (
              <p className="mt-3 text-body text-med">
                {current
                  ? 'The schedule has more than one row for this run, so the server will refuse the change. Remove the extra row on the server first.'
                  : 'The schedule has no row for this run, so the server will refuse the change. Add the row on the server first.'}
              </p>
            )}
            {changed && next && valid && (
              <p className="mt-3 text-body text-ink">
                After saving: <span className="font-semibold tabular-nums">{describeRun(slot.run.value, next)}</span>
              </p>
            )}
          </fieldset>
        ) : (
          <p className="text-body text-graphite">Pick a team and run above to change when it starts.</p>
        )}

        {submitError && (
          <div role="alert" className="px-3 py-2.5 rounded-control border border-crit/40 bg-crit-tint">
            <p className="text-body font-semibold text-crit">The schedule could not be saved</p>
            <p className="mt-1 font-mono text-small text-ink break-words">{submitError}</p>
          </div>
        )}
      </form>
    </SidePanel>
  );
}
